// Admin → Scanner inboxes: the copier sends scans straight to the app (WebDAV over HTTPS). Each inbox has a key, shown here once
// when it is made or replaced, that goes in the copier's folder path. Scans become import batches on the Imports page.
import { useState, type FormEvent } from 'react';
import { ErrorBox } from '../components/Layout.tsx';
import { post } from '../lib/api.ts';
import { useData } from '../lib/useData.ts';

interface Inbox { name: string; signMode: 'none' | 'app' | 'paper' | 'auto'; createdAt: string; createdBy: string | null; revokedAt: string | null; lastUsedAt: string | null; uploads: number }
const when = (s: string | null) => (s ? new Date(s).toLocaleString() : '—');

export function ScanInboxes() {
  const { data, error, reload } = useData<Inbox[]>('/api/admin/scan-inboxes');
  const [name, setName] = useState('copier');
  const [signMode, setSignMode] = useState<'none' | 'app' | 'paper' | 'auto'>('auto');
  const [shown, setShown] = useState<{ name: string; key: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [copiedAddr, setCopiedAddr] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<{ name: string; key: string } | unknown>) => {
    setErr(null); setBusy(true); setCopied(false); setCopiedAddr(false);
    try {
      const r = await fn();
      if (r && typeof r === 'object' && 'key' in r) setShown(r as { name: string; key: string });
      await reload();
    } catch (x) { setErr(x); } finally { setBusy(false); }
  };
  const create = (e: FormEvent) => { e.preventDefault(); void run(() => post('/api/admin/scan-inboxes', { name, signMode })); };
  const rotate = (n: string) => {
    if (!window.confirm(`Give "${n}" a new key? The old key stops working at once: the copier's folder path must be changed to the new one.`)) return;
    void run(() => post(`/api/admin/scan-inboxes/${n}/rotate`));
  };
  const revoke = (n: string) => {
    if (!window.confirm(`Turn off "${n}"? The copier can no longer send scans to it (giving it a new key turns it back on).`)) return;
    void run(() => post(`/api/admin/scan-inboxes/${n}/revoke`));
  };
  const host = `${location.protocol}//${location.host}`;
  const folder = shown ? `/scan-inbox/t/${shown.key}` : '';

  return (
    <>
      <h1>Scanner inboxes</h1>
      <p className="muted" style={{ marginTop: 0 }}>The copier, and desk PCs with their own scanner, send each scan straight to the app over an encrypted connection. Give each desk its own inbox (e.g. <i>desk-front</i>). Scans appear on the
        Imports page as “From the scanner inbox”; cover sheets on the piles file them (or choose their event there). Only NGF’s office network can send to an inbox, and only with its key.</p>
      <ErrorBox error={error ?? err} />

      {shown && (
        <div className="card scan-key">
          <h2 style={{ marginTop: 0 }}>Key for “{shown.name}”</h2>
          <p><b>This key is shown only now.</b> Enter it in the copier, then close this box. It is not stored anywhere it can be read back; if it is
            lost, give the inbox a new key.</p>
          <p className="muted small" style={{ marginBottom: 4 }}>On the copier (Remote UI → Address Book → the destination, type File, protocol WebDAV):</p>
          <table className="scan-key-table"><tbody>
            <tr><th>Host Name</th><td><code>{host}</code></td></tr>
            <tr><th>Folder Path</th><td><code>{folder}</code></td></tr>
            <tr><th>User Name / Password</th><td className="muted">leave as they are (this copier does not send them)</td></tr>
          </tbody></table>
          <div className="row" style={{ marginTop: '0.75rem' }}>
            <button type="button" onClick={() => void navigator.clipboard.writeText(folder).then(() => setCopied(true))}>{copied ? 'Copied' : 'Copy folder path'}</button>
          </div>
          <p className="muted small" style={{ margin: '1rem 0 4px' }}>On a desk PC with its own scanner (one inbox per desk), the whole address:</p>
          <table className="scan-key-table"><tbody>
            <tr><th>Inbox address</th><td><code>{host}{folder}</code></td></tr>
          </tbody></table>
          <div className="row" style={{ marginTop: '0.5rem' }}>
            <button type="button" className="secondary" onClick={() => void navigator.clipboard.writeText(`${host}${folder}`).then(() => setCopiedAddr(true))}>{copiedAddr ? 'Copied' : 'Copy inbox address'}</button>
          </div>
          <ul className="small" style={{ marginTop: '0.5rem' }}>
            <li><b>Network folder:</b> in File Explorer, <i>This PC → Map network drive</i>, paste the address as the folder, and set the scanner
              software to save PDFs there.</li>
            <li><b>Or the sender:</b> download <a href="/desk-scanner/install.ps1" download>install.ps1</a>, right-click → <i>Run with PowerShell</i> as
              the person who scans, and paste the address. The scanner software then saves to <code>C:\Scans\NGF</code>; scans are sent within a minute.</li>
          </ul>
          <div className="row" style={{ marginTop: '0.75rem' }}>
            <button type="button" className="secondary" onClick={() => setShown(null)}>I have entered it, close</button>
          </div>
        </div>)}

      <div className="card">
        {!data ? <p className="muted">Loading…</p> : !data.length ? <p className="muted">No scanner inbox yet.</p> : (
          <div className="table-scroll"><table>
            <thead><tr><th>Name</th><th>Sent scans</th><th>Last scan</th><th>Signing</th><th>Made</th><th></th></tr></thead>
            <tbody>{data.map((i) => (
              <tr key={i.name}>
                <td><b>{i.name}</b>{i.revokedAt && <div className="muted small">turned off {when(i.revokedAt)}</div>}</td>
                <td>{i.uploads}</td>
                <td className="nowrap">{when(i.lastUsedAt)}</td>
                <td>{i.signMode === 'auto' ? 'By form' : i.signMode === 'paper' ? 'Signed on paper' : i.signMode === 'app' ? 'Signed in the app' : 'No signature needed'}</td>
                <td className="small">{when(i.createdAt)}{i.createdBy ? ` by ${i.createdBy}` : ''}</td>
                <td className="nowrap">
                  <button type="button" className="secondary small" disabled={busy} onClick={() => rotate(i.name)}>{i.revokedAt ? 'Turn on with a new key' : 'New key'}</button>{' '}
                  {!i.revokedAt && <button type="button" className="secondary small" disabled={busy} onClick={() => revoke(i.name)}>Turn off</button>}
                </td>
              </tr>))}</tbody>
          </table></div>)}
      </div>

      <form className="card" onSubmit={create}>
        <h2 style={{ marginTop: 0 }}>Add an inbox</h2>
        <div className="row">
          <div className="field"><label htmlFor="si-name">Name</label>
            <input id="si-name" value={name} onChange={(e) => setName(e.target.value.toLowerCase())} placeholder="copier" /></div>
          <div className="field"><label htmlFor="si-sign">Scanned visits are</label>
            <select id="si-sign" value={signMode} onChange={(e) => setSignMode(e.target.value as 'none' | 'app' | 'paper' | 'auto')}>
              <option value="auto">By form (usual): physicals signed on paper, COHSG screenings no signature</option>
              <option value="paper">All signed on paper</option>
              <option value="none">Not signed (documents, no physician signature needed)</option>
              <option value="app">Signed in the app (to the sign queue)</option>
            </select></div>
          <div style={{ flex: '0 0 auto', alignSelf: 'end' }}><button type="submit" disabled={busy || !name.trim()}>Add and show the key</button></div>
        </div>
      </form>
    </>
  );
}
